import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { Download, X } from 'lucide-react';
import { setupInstallPrompt, isInstalled } from '../utils/pwa';

const Banner = styled.div`
  position: fixed;
  bottom: ${theme.spacing.xl};
  left: ${theme.spacing.xl};
  right: ${theme.spacing.xl};
  background: ${theme.colors.surface};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.xl};
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    left: ${theme.spacing.md};
    right: ${theme.spacing.md};
    bottom: ${theme.spacing.md};
    flex-direction: column;
    text-align: center;
  }
`;

const BannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const BannerTitle = styled.div`
  font-size: ${theme.fontSize.lg};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.primary};
  font-family: 'Inter', sans-serif;
`;

const BannerText = styled.div`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  font-family: 'Roboto', sans-serif;
`;

const InstallButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.semibold};
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${theme.colors.primaryDark};
    transform: translateY(-1px);
  }
  
  &:focus-visible {
    outline: 3px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    color: ${theme.colors.text};
  }
  
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${theme.borderRadius.sm};
  }
`;

const STORAGE_KEY = 'treinopro_pwa_install_dismissed';

export const PwaInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    // Não mostrar se já estiver instalado
    if (isInstalled()) {
      return;
    }

    // Verificar se foi dispensado
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      return;
    }

    // Configurar o prompt de instalação
    setupInstallPrompt((showPromptFn) => {
      setInstallPrompt(() => showPromptFn);
      setShowPrompt(true);
    });
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      await installPrompt();
      setShowPrompt(false);
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!showPrompt || !installPrompt) {
    return null;
  }

  return (
    <Banner role="dialog" aria-labelledby="pwa-install-title" aria-describedby="pwa-install-description">
      <CloseButton onClick={handleDismiss} aria-label="Fechar">
        <X size={20} />
      </CloseButton>
      <BannerContent>
        <BannerTitle id="pwa-install-title">Instalar TITAN MODE</BannerTitle>
        <BannerText id="pwa-install-description">
          Instale o app para acesso rápido e funcionamento offline
        </BannerText>
      </BannerContent>
      <InstallButton onClick={handleInstall}>
        <Download size={18} />
        Instalar
      </InstallButton>
    </Banner>
  );
};

